import React, { useState, useCallback, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Typography, Select, MenuItem, Box, Grid, TextField, FormLabel, Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AdjustIcon from '@mui/icons-material/Adjust';
import SignatureCanvas from "react-signature-canvas";
import { useMemo } from 'react';

const ConditionRow = React.memo(({ item, mode, onChangeCondition, isPreEntrega }) => {
  const opts = [
    { value: "Buena",     label: "Buena",                        bg: "#2E7D32", fg: "#fff" },
    { value: "Mala",      label: "Mala",                         bg: "#C62828", fg: "#fff" },
    { value: "Efectuada", label: isPreEntrega ? "Realizado" : "Efectuada", bg: "#ED9519", fg: "#1a0e00" },
  ];
  return (
    <TableRow sx={{ "&:hover": { bgcolor: "action.hover" } }}>
      <TableCell sx={{ fontSize: "0.82rem" }}>{item.descripcion}</TableCell>
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          {opts.map(opt => {
            const isActive = item.condicion === opt.value;
            return (
              <Button key={opt.value} size="small"
                variant={isActive ? "contained" : "outlined"}
                disabled={mode === "view"}
                onClick={() => onChangeCondition(item.id, opt.value)}
                sx={{
                  minWidth: 0, px: 1.2, py: 0.3, fontSize: "0.7rem", fontWeight: 600,
                  textTransform: "none", whiteSpace: "nowrap",
                  ...(isActive
                    ? { bgcolor: opt.bg, color: opt.fg, borderColor: opt.bg, "&:hover": { bgcolor: opt.bg } }
                    : { color: "text.secondary", borderColor: "divider" }),
                }}
              >
                {opt.label}
              </Button>
            );
          })}
        </Box>
      </TableCell>
    </TableRow>
  );
});
ConditionRow.displayName = "ConditionRow";

const Detalle = ({
  mode, handleConditionChange, selectedCondition, setSelectedCondition,
  firmaI, setFirmaI, aclaracionI, setAclaracionI,
  firmaC, setFirmaC, aclaracionC, setAclaracionC,
  comnetarioI, setComnetarioI, comnetarioC, setComnetarioC,
  condiciones, signatureOnly = false,
}) => {
  const padRef = useRef(null);
  const [canvasVisible, setCanvasVisible] = useState(false);

  const clearCanvas = useCallback(() => {
    padRef.current && padRef.current.clear();
    setFirmaC(""); setCanvasVisible(false);
  }, [setFirmaC]);

  const saveCanvas = useCallback(() => {
    setFirmaC(padRef.current ? padRef.current.toDataURL() : "");
    setCanvasVisible(true);
  }, [setFirmaC]);

  const groupedConditions = useMemo(() => {
    return (condiciones || []).reduce((acc, item) => {
      if (!acc[item.seccion]) acc[item.seccion] = [];
      acc[item.seccion].push(item);
      return acc;
    }, {});
  }, [condiciones]);

  const stableHandleChange = useCallback((id, val) => handleConditionChange(id, val), [handleConditionChange]);

  return (
    <>
      {!signatureOnly && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="subtitle2" fontWeight={600} mb={1} color="text.secondary">Condicion de servicio</Typography>
          <Select value={selectedCondition} onChange={e => setSelectedCondition(e.target.value)}
            displayEmpty size="small" fullWidth disabled={mode === "view"}>
            <MenuItem value=""><em>Seleccionar...</em></MenuItem>
            <MenuItem value="P">P - Pre Entrega</MenuItem>
            <MenuItem value="E">E - Entrega</MenuItem>
            <MenuItem value="1">1 - 250 hs</MenuItem>
            <MenuItem value="2">2 - 500 hs</MenuItem>
            <MenuItem value="3">3 - 1000 hs</MenuItem>
            <MenuItem value="4">4 - 1250 hs</MenuItem>
            <MenuItem value="5">5 - mas de 1250 hs</MenuItem>
            <MenuItem value="U">U - Entrega Usado</MenuItem>
            <MenuItem value="X">X - Entrega Adicional</MenuItem>
          </Select>
        </Box>
      )}

      {!signatureOnly && (
        <Box sx={{ px: 2, pb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          {[
            { icon: <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />, label: "Buena" },
            { icon: <CancelIcon     sx={{ fontSize: 16, color: "#C62828" }} />, label: "Mala" },
            { icon: <AdjustIcon     sx={{ fontSize: 16, color: "#ED9519" }} />, label: selectedCondition === "P" ? "Realizado" : "Efectuada" },
          ].map(({ icon, label }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {icon}
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {!signatureOnly && (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { bgcolor: "#1A1A2E", color: "#fff", fontWeight: 700 } }}>
                <TableCell>Item</TableCell>
                <TableCell align="center">Estado {selectedCondition && <span style={{ color: "#ED9519" }}>- {selectedCondition}</span>}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedCondition ? (
                Object.keys(groupedConditions).map(seccion => (
                  <React.Fragment key={seccion}>
                    <TableRow>
                      <TableCell colSpan={2} sx={{ bgcolor: "#ED9519", py: 0.6, px: 2 }}>
                        <Typography variant="caption" fontWeight={700} color="#4a2900" textTransform="uppercase" letterSpacing={0.5}>{seccion}</Typography>
                      </TableCell>
                    </TableRow>
                    {groupedConditions[seccion].map(item => (
                      <ConditionRow key={item.id} item={item} mode={mode} onChangeCondition={stableHandleChange} isPreEntrega={selectedCondition === "P"} />
                    ))}
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 3, color: "text.secondary", fontStyle: "italic" }}>
                    Selecciona una condicion de servicio para ver los items
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Grid container spacing={2} sx={{ px: 1, mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" fontWeight={600} mb={0.5}>Comentarios inspector</Typography>
          <TextField fullWidth multiline rows={3} size="small" placeholder="Observaciones del inspector..."
            disabled={mode === "view"} value={comnetarioI} onChange={e => setComnetarioI(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" fontWeight={600} mb={0.5}>Comentarios cliente</Typography>
          <TextField fullWidth multiline rows={3} size="small" placeholder="Observaciones del cliente..."
            disabled={mode === "view"} value={comnetarioC} onChange={e => setComnetarioC(e.target.value)} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 2, mb: 2 }}>
        <Box sx={{ bgcolor: "#ED9519", borderRadius: 1, py: 1, px: 2, mb: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography variant="subtitle2" fontWeight={700} color="#4a2900">Manifiesta conforme con la inspeccion efectuada</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: "0.8rem", fontStyle: "italic" }}>
          El equipo queda en servicio en condiciones satisfactorias. Se ha dado la informacion adecuada sobre su
          funcionamiento, cuidado, mantenimiento y operacion, y se entregan los manuales correspondientes.
        </Typography>
        <Grid container spacing={2}>
          {selectedCondition !== "P" && (
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ fontWeight: 600, fontSize: "0.82rem", mb: 1, display: "block" }}>Firma del cliente</FormLabel>
              <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1, display: "inline-block", mb: 1, bgcolor: "#fff" }}>
                <SignatureCanvas ref={padRef} canvasProps={{ width: 280, height: 150 }} />
              </Box>
              <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                <Button size="small" variant="outlined" onClick={clearCanvas} disabled={mode === "view"}>Limpiar</Button>
                <Button size="small" variant="contained"
                  sx={{ bgcolor: "#ED9519", "&:hover": { bgcolor: "#c97d0e" }, color: "#1a0e00" }}
                  onClick={saveCanvas} disabled={mode === "view"}>
                  Guardar firma
                </Button>
              </Box>
              {canvasVisible && <img src={firmaC} alt="Firma cliente" style={{ maxWidth: 280, border: "1px solid #eee", borderRadius: 4 }} />}
            </Grid>
          )}
          <Grid item xs={12} sm={selectedCondition === "P" ? 12 : 6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Inspector" size="small" fullWidth value={firmaI} onChange={e => setFirmaI(e.target.value)} disabled />
              </Grid>
              {selectedCondition !== "P" && (
                <Grid item xs={12}>
                  <TextField label="Aclaracion firma cliente" size="small" fullWidth value={aclaracionC}
                    onChange={e => setAclaracionC(e.target.value)} disabled={mode === "view"} required />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField label="Aclaracion inspector" size="small" fullWidth value={aclaracionI}
                  onChange={e => setAclaracionI(e.target.value)} disabled={mode === "view"} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default React.memo(Detalle);
